const API_BASE = import.meta.env.VITE_API_URL ?? '';
const API_KEY_STORAGE_KEY = 'cadence_api_key';

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body: { error?: string; message?: string; statusCode?: number },
  ) {
    super(body.message ?? statusText);
    this.name = 'ApiError';
  }
}

// ─── API Key management (dev mode) ────────────────────────────────────
export function getApiKey(): string | null {
  return localStorage.getItem(API_KEY_STORAGE_KEY);
}

export function setApiKey(key: string): void {
  localStorage.setItem(API_KEY_STORAGE_KEY, key);
}

export function clearApiKey(): void {
  localStorage.removeItem(API_KEY_STORAGE_KEY);
}

export function isDevAuthMode(): boolean {
  return !!getApiKey();
}

// ─── Token refresh (cookie auth mode) ─────────────────────────────────
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

async function tryRefreshToken(): Promise<boolean> {
  if (isRefreshing && refreshPromise) return refreshPromise;
  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

// ─── Core request function ────────────────────────────────────────────
async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const hasBody = options?.body !== undefined;
  const apiKey = getApiKey();

  const headers: Record<string, string> = {
    ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
    // If API key is set (dev mode), use Bearer auth instead of cookies
    ...(apiKey ? { 'Authorization': `Bearer ${apiKey}` } : {}),
    ...(options?.headers as Record<string, string> ?? {}),
  };

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  // 401 handling
  if (res.status === 401 && !path.includes('/auth/')) {
    // In API key mode, don't try cookie refresh — just throw
    if (apiKey) {
      const body = await res.json().catch(() => ({}));
      throw new ApiError(401, 'Unauthorized', body);
    }
    // Cookie mode: try refresh token, then retry once
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      const retryRes = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers,
        credentials: 'include',
      });
      if (retryRes.ok) {
        if (retryRes.status === 204) return undefined as T;
        return retryRes.json();
      }
    }
    const body = await res.json().catch(() => ({}));
    throw new ApiError(401, 'Unauthorized', body);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, res.statusText, body);
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', ...(body !== undefined ? { body: JSON.stringify(body) } : {}) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
